import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';

// Public endpoints that authenticate themselves — no CSRF wait.
const CSRF_EXCLUDE = ['/user/login', '/user/register', '/user/password/reset', '/user/csrf-token'];
const UNSAFE = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Cookie-session + double-submit CSRF interceptor for the accountant app. Every API call sends
 * credentials so the browser returns the session cookie; mutating requests echo the in-memory CSRF
 * token. On a real 401 the session is cleared and the user is sent to login.
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!req.url.startsWith(environment.apiBaseUrl)) {
    return next(req);
  }

  // Always send the session cookie; additionally attach the dev-only Bearer token when present
  // (getAccessToken returns null in production, so prod stays cookie-only).
  const accessToken = auth.getAccessToken();
  const baseReq = req.clone({
    withCredentials: true,
    ...(accessToken ? { setHeaders: { Authorization: `Bearer ${accessToken}` } } : {}),
  });
  const isPublic = CSRF_EXCLUDE.some((p) => req.url.includes(p));
  const needsCsrf = !isPublic && UNSAFE.includes(req.method);

  const withCsrf = (r: HttpRequest<unknown>): HttpRequest<unknown> => {
    const csrf = auth.csrfToken();
    return needsCsrf && csrf ? r.clone({ setHeaders: { 'x-csrf-token': csrf } }) : r;
  };

  const onUnauthorized = (): void => {
    if (isPublic) return;
    sessionStorage.removeItem('acc_signed_in');
    if (!router.url.includes('/auth/login')) router.navigate(['/auth/login']);
  };

  const send = (): Observable<HttpEvent<unknown>> =>
    next(withCsrf(baseReq)).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) onUnauthorized();
        return throwError(() => error);
      }),
    );

  if (needsCsrf && !auth.csrfToken()) {
    return from(auth.ensureCsrfReady()).pipe(
      switchMap(() => {
        if (!auth.csrfToken()) {
          return throwError(() => new Error('CSRF token unavailable; mutating request blocked.'));
        }
        return send();
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) onUnauthorized();
        return throwError(() => error);
      }),
    );
  }

  return send();
};
